import type { BossStatus } from '../../core/boss/boss-fight';
import type { BossObjectShape } from '../../data/boss-objects';

/**
 * Pure mappings of the boss visuals (GDD §9.1, §9.2, issue #96): from the state of the fight to the pose of the
 * model and from the `shape` of an attack object (data of the core) to the kind of geometry that draws it.
 */
export type BossPose = 'idle' | 'transition' | 'retreat' | 'landed';
export type GeometryKind = 'disc' | 'bullet' | 'plush' | 'horseshoe' | 'portrait' | 'piece' | 'column' | 'barrel';

export function bossPose(status: BossStatus, retreatLeft: number): BossPose {
  if (status === 'transition') return 'transition';
  if (status === 'done' || (status === 'retreating' && retreatLeft <= 0)) return 'landed';
  return status === 'retreating' ? 'retreat' : 'idle';
}

/** disc = flat shiny cylinder, bullet = elongated dark capsule, plush = the toy hippo of El Patrón. */
export function shapeToGeometryKind(shape: BossObjectShape): GeometryKind {
  switch (shape) {
    case 'disc':
      return 'disc';
    case 'bullet':
      return 'bullet';
    case 'plush':
      return 'plush';
    case 'horseshoe':
      return 'horseshoe';
    case 'portrait':
      return 'portrait';
    case 'piece':
      return 'piece';
    case 'column':
      return 'column';
    case 'barrel':
      return 'barrel';
  }
}

/** Progress 0..1 of the comic retreat, from the ticks left: 0 at its start, 1 when the helicopter has landed. */
export const retreatProgress = (retreatLeft: number, total: number): number => Math.min(1, Math.max(0, 1 - retreatLeft / total));

/** Ease-out so the helicopter slows down as it touches the pad. */
export const easeOut = (t: number): number => 1 - (1 - t) * (1 - t);
